import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  Calculator,
  BarChart2,
  Sigma,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for Maths tuition in Ghazipur. Please share Maths course details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "340+", label: "Students Enrolled", icon: Users },
  { value: "95%", label: "Board Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
  { value: "8–12", label: "Classes We Teach", icon: BookOpen },
];

const MATHS_TOPICS = [
  { class: "Class 8", topics: "Algebra, Geometry, Data Handling, Mensuration, Rational Numbers, Squares & Cubes & more" },
  { class: "Class 9", topics: "Number Systems, Polynomials, Lines & Angles, Triangles, Coordinate Geometry, Statistics & more" },
  { class: "Class 10", topics: "Real Numbers, Quadratic Equations, Arithmetic Progressions, Circles, Trigonometry, Probability & more" },
  { class: "Class 11", topics: "Sets, Functions, Trigonometry, Complex Numbers, Permutations, Straight Lines, Conic Sections, Calculus & more" },
  { class: "Class 12", topics: "Relations & Functions, Matrices, Determinants, Continuity, Integration, Differential Equations, Vectors, Probability & more" },
];

const WHY_SSS_MATHS = [
  {
    icon: Brain,
    title: "Concept-First Approach",
    desc: "Every Maths concept is taught from the ground up — we build strong fundamentals before tackling advanced problems, eliminating fear of Maths.",
  },
  {
    icon: Calculator,
    title: "Daily Problem Practice",
    desc: "Structured daily practice with graded problem sets — from NCERT basics to HOTS questions — ensures exam-ready confidence.",
  },
  {
    icon: Zap,
    title: "Speed & Accuracy Training",
    desc: "Timed drills and shortcut techniques help students solve Maths problems faster without compromising accuracy in board exams.",
  },
  {
    icon: Users,
    title: "Small Batches",
    desc: "Small batch sizes ensure every student's doubts are addressed. No student is left behind in our Maths classes.",
  },
  {
    icon: BarChart2,
    title: "Weekly Tests & Progress Tracking",
    desc: "Chapter-wise tests, full syllabus mock exams and detailed performance analytics help identify weak areas instantly.",
  },
  {
    icon: Shield,
    title: "Dedicated Doubt Sessions",
    desc: "Special doubt-clearing sessions for Maths are held regularly. Students can clarify any concept without hesitation.",
  },
  {
    icon: Sigma,
    title: "NCERT + Extra Practice Sheets",
    desc: "Complete NCERT solutions, chapter-wise practice sheets, previous-year board questions and sample papers — all included.",
  },
  {
    icon: Award,
    title: "Proven Track Record",
    desc: "SSS Achievers students consistently score 90%+ in Maths in CBSE boards — backed by results since 2025.",
  },
];

const TESTIMONIALS = [
  {
    name: "Anjali Verma",
    cls: "Class 10, 2025",
    rating: 5,
    text: "I used to fear Maths but SSS Achievers completely changed that. Their concept-based teaching and daily practice made me score 96% in board Maths. Best Maths tuition in Ghazipur!",
  },
  {
    name: "Rohit Sinha",
    cls: "Class 12, 2025",
    rating: 5,
    text: "Calculus and Integration seemed impossible until I joined SSS Achievers. The faculty explains everything step-by-step. I scored 91 in Class 12 Maths!",
  },
  {
    name: "Kavita Yadav (Parent)",
    cls: "Parent of Class 9 Student",
    rating: 5,
    text: "My son's Maths improved from 55% to 87% in one academic year at SSS Achievers. The weekly tests and personalised attention are outstanding. Highly recommend!",
  },
  {
    name: "Deepak Kumar",
    cls: "Class 9, 2025",
    rating: 5,
    text: "Small batches mean the teacher actually knows where I'm struggling. SSS Achievers is the best Maths tuition in Ghazipur, no doubt.",
  },
];

const COMPARISON = [
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "50+ students (crowded)" },
  { feature: "Daily Practice Sheets", sss: "Included every class", others: "Not provided" },
  { feature: "Doubt-Clearing Sessions", sss: "Dedicated Maths sessions", others: "Limited or extra fee" },
  { feature: "Mock Tests", sss: "Weekly chapter + full tests", others: "Monthly or less frequent" },
  { feature: "NCERT Coverage", sss: "100% + extra practice", others: "Partial" },
  { feature: "Demo Class", sss: "Free before enrollment", others: "Usually not offered" },
  { feature: "Parent Progress Report", sss: "Monthly detailed reports", others: "Rarely communicated" },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function MathsTuitionGhazipur() {
  return (
    <>
      <Helmet>
        <title>Maths Tuition in Ghazipur | Expert Maths Coaching Class 8–12 – SSS Achievers</title>
        <meta
          name="description"
          content="Looking for the best Maths tuition in Ghazipur? SSS Achievers offers expert Maths coaching for Class 8–12. Concept-based teaching, daily practice, 95% success rate. Free demo class. Enroll now!"
        />
        <meta
          name="keywords"
          content="maths tuition in Ghazipur, maths coaching Ghazipur, best maths tuition Ghazipur, maths classes Ghazipur UP, class 10 maths tuition Ghazipur, class 12 maths coaching Ghazipur, CBSE maths coaching Ghazipur, maths teacher Ghazipur, SSS Achievers maths, maths tuition near me Ghazipur"
        />
        <link rel="canonical" href="https://www.sssachievers.com/maths-tuition-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Maths Tuition in Ghazipur | Expert Coaching – SSS Achievers" />
        <meta property="og:description" content="Best Maths tuition in Ghazipur for Class 8–12. Concept-based teaching, 95% success rate, small batches. Free demo class at SSS Achievers." />
        <meta property="og:url" content="https://www.sssachievers.com/maths-tuition-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Maths Tuition in Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="Expert Maths coaching in Ghazipur for Class 8–12. 95% success rate, free demo class." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Best Maths Tuition Ghazipur", "Maths Coaching Ghazipur"],
                "description": "SSS Achievers offers the best Maths tuition in Ghazipur for Class 8–12 with concept-based teaching, daily practice, and a 95% success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best Maths tuition in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is the top-rated Maths tuition centre in Ghazipur. With concept-based teaching, small batch sizes, daily practice sheets and a 95% success rate in CBSE boards, SSS Achievers is the #1 choice for Maths coaching in Ghazipur for Class 8–12."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers offer Maths tuition for Class 10 in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. SSS Achievers provides expert Maths coaching specifically for Class 10 CBSE board preparation in Ghazipur. The curriculum covers all NCERT chapters, board exam pattern questions, previous year papers and full mock tests."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How does SSS Achievers teach Maths differently?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers uses a concept-first approach where every topic is built from the ground up. We combine theory with daily graded problem practice, speed drills, doubt-clearing sessions and weekly tests — ensuring students gain both understanding and exam readiness."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo Maths class available at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class for Maths. You can attend a session, experience the teaching style and meet the faculty before enrolling."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the batch size for Maths tuition at SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Each Maths batch at SSS Achievers has a intentionally small, ensuring individual attention, focused teaching and regular one-on-one feedback from the faculty."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers cover Class 12 Maths including Calculus?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Absolutely. SSS Achievers covers the complete Class 12 Maths syllabus including Relations & Functions, Matrices, Determinants, Continuity, Differentiation, Integration, Differential Equations, Vectors and Probability — with expert step-by-step teaching."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Maths Tuition in Ghazipur", "item": "https://www.sssachievers.com/maths-tuition-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Maths Tuition in Ghazipur</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">Ghazipur's Most Trusted Maths Tuition</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best <span className="text-[#0097b2]">Maths Tuition</span> in Ghazipur for Class 8–12
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers makes Maths simple, structured and scoring. Our expert Maths faculty in Ghazipur uses a concept-first approach backed by daily practice, weekly tests and personalised attention to turn Maths from a fear into a strength.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={WA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition"
                  >
                    Book Free Demo Maths Class <ArrowRight className="w-4 h-4" />
                  </a>
                  <a
                    href={`tel:${PHONE}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition"
                  >
                    <Phone className="w-4 h-4" />
                    Call Now: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["Free Demo Class", "NCERT + Extra Practice", "Small Batches", "95% Success Rate"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-2 gap-4"
              >
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Based on 340+ student reviews</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Top Rated</p>
                    <p className="text-3xl font-bold">Maths</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ WHY SSS FOR MATHS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-sm font-semibold text-[#0097b2] uppercase tracking-wider mb-2">
                Why Students Choose SSS
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Why SSS Achievers is the <span className="text-[#0097b2]">Best Maths Tuition</span> in Ghazipur
              </motion.h2>
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mt-4 text-slate-600 max-w-2xl mx-auto text-base">
                We transform Maths from a subject students fear into one they score highest in.
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {WHY_SSS_MATHS.map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  custom={i}
                  variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ MATHS TOPICS BY CLASS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Our Maths Curriculum — <span className="text-[#0097b2]">Class 8 to 12</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">Everything we teach in Maths — NCERT-aligned, exam-focused, extra practice included</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {MATHS_TOPICS.map(({ class: cls, topics }, i) => (
                <motion.div
                  key={cls}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  custom={i}
                  variants={fade}
                  className="bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-[#0097b2]/40 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0097b2]/10 flex items-center justify-center">
                      <Calculator className="w-5 h-5 text-[#0097b2]" />
                    </div>
                    <h3 className="font-bold text-slate-800">{cls} Maths</h3>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed">{topics}</p>
                </motion.div>
              ))}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                custom={5}
                variants={fade}
                className="bg-gradient-to-br from-[#0097b2] to-[#006f85] rounded-2xl p-6 text-white flex flex-col justify-center"
              >
                <h3 className="font-bold text-xl mb-2">CBSE &amp; UP Board</h3>
                <p className="text-blue-100 text-sm mb-4">Maths coaching for both CBSE and UP Board with NCERT-aligned curriculum and board exam-oriented practice.</p>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0097b2] bg-white px-4 py-2 rounded-lg hover:bg-blue-50 transition w-fit">
                  Enquire Now <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ COMPARISON TABLE ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers Maths vs. Other Tuitions in Ghazipur
              </motion.h2>
              <p className="mt-3 text-slate-500 text-sm max-w-xl mx-auto">See why SSS Achievers is the preferred Maths tuition for students across Ghazipur</p>
            </div>

            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Others</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                What Students Say About <span className="text-[#0097b2]">Maths Tuition</span> at SSS Achievers
              </motion.h2>
              <p className="mt-3 text-slate-500 text-sm">Real results from Ghazipur students</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div
                  key={name}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  custom={i}
                  variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col"
                >
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ CAMPUS GLIMPSE ══ */}
        <section className="py-14 md:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold"
              >
                Inside <span className="text-[#0097b2]">SSS Achievers Ghazipur</span> — Where Maths Becomes Easy
              </motion.h2>
              <p className="mt-2 text-slate-500 text-sm">Our classrooms, campus and student success moments</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  src: "/images/SSS%20Achievers%20classroom.avif",
                  alt: "SSS Achievers Maths coaching classroom in Ghazipur — smart AI-powered learning environment where expert Maths tuition is delivered for Class 8-12",
                  caption: "🏫 Maths Classroom — SSS Achievers Ghazipur",
                },
                {
                  src: "/images/gallery/sunday-special-class.jpg",
                  alt: "SSS Achievers Sunday special Maths class in Ghazipur — extra Maths practice sessions held every Sunday for Class 8-12 students at the best Maths tuition centre",
                  caption: "📚 Sunday Special Maths Class — SSS Achievers",
                },
                {
                  src: "/images/gallery/class-test.jpg",
                  alt: "SSS Achievers weekly Maths class test in Ghazipur — students appearing in chapter-wise Maths tests at the best Maths tuition in Ghazipur",
                  caption: "📝 Weekly Maths Class Test in Progress",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="224"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ══ LOCATION ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Visit Us for the Best <span className="text-[#0097b2]">Maths Tuition in Ghazipur</span>
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  SSS Achievers is conveniently located in Ghazipur, easily accessible from Sadar, Zamania, Saidpur and all surrounding areas. Our state-of-the-art facilities ensure the best learning environment for Maths students.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                    { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                    { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                  ].map(({ icon: Icon, text, href }) => (
                    <div key={text} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#0097b2]" />
                      </div>
                      {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md text-sm">
                    Book Free Demo <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/courses/class-10-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition text-sm">
                    View Class 10 Course
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">Find Us in Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers Maths Tuition Ghazipur Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Frequently Asked Questions
              </motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About Maths tuition in Ghazipur at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Which is the best Maths tuition in Ghazipur?", a: "SSS Achievers is the top-rated Maths tuition centre in Ghazipur. With concept-based teaching, small batch sizes, daily practice sheets and a 95% board success rate, SSS Achievers is the #1 choice for Maths coaching in Ghazipur for Class 8–12 students." },
                { q: "Does SSS Achievers offer Maths coaching for Class 10 board exam?", a: "Yes. SSS Achievers provides dedicated Class 10 CBSE Maths coaching in Ghazipur covering all NCERT chapters, board exam pattern questions, previous year papers, full mock tests and answer-writing practice." },
                { q: "How is SSS Achievers' Maths teaching different from others?", a: "SSS Achievers uses a concept-first teaching methodology combined with daily graded problem practice, speed drills, shortcut techniques and dedicated doubt-clearing sessions — ensuring students understand every topic thoroughly and score high in exams." },
                { q: "Is a free demo Maths class available?", a: "Yes — SSS Achievers offers a completely free demo Maths class before enrollment. Attend a session, meet the Maths faculty and experience our teaching quality before making any decision." },
                { q: "What is the batch size for Maths at SSS Achievers?", a: "Maths batches at SSS Achievers are kept intentionally small, ensuring every student receives individual attention, personalised feedback and focused teaching." },
                { q: "Does SSS Achievers provide study materials for Maths?", a: "Yes — printed practice sheets, NCERT solutions, chapter-wise exercises, HOTS questions, previous year board papers and revision sheets are all included in the course fee at SSS Achievers." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Ghazipur's #1 Maths Tuition</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Start Your Maths Journey at SSS Achievers Today
              </h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Limited seats per batch. Book your free demo Maths class now and experience why 340+ students trust SSS Achievers Ghazipur.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo Maths Class <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
